import {NyModal} from 'nyse-web-tools-common/lib/react';
import connect from 'nyse-web-tools-common/lib/utils/redux.connect';
import PropTypes from 'prop-types';
import React, {useEffect, useRef, useState} from 'react';

import {deleteMmAppointments} from '../../../stores/mmAppointments/mmAppointments.actions';
import {
    findDuplicateAppointmentIndexes,
    summarizeBatchResult
} from '../../../stores/mmAppointments/mmAppointments.helpers';

const DeleteConfirmationModalComponent = (props) => {
    const {
        userCrd,
        selectedRows,
        show,
        hideModal,
        onClose,
        deleteMmAppointments,
        mmAppointments
    } = props;

    const isMountedRef = useRef(false);
    const hadPartialSuccessRef = useRef(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [errorLines, setErrorLines] = useState([]);
    // Rows still to attempt. Starts as the full selection; after a partial
    // success it is narrowed to just the rows that failed, so a retry re-sends
    // ONLY those — never the rows GBO already accepted. null = use selectedRows.
    const [rowsToDelete, setRowsToDelete] = useState(null);
    // True once an attempt resolves with EVERY row failing. The "Are you sure?"
    // prompt is then stale and the only sensible action is Close — re-submitting
    // an all-rejected set (e.g. CRS-managed SLMM, ineligible for self-service)
    // just round-trips to the identical failure.
    const [allFailed, setAllFailed] = useState(false);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const isDeleting = mmAppointments.isDeleting;
    const pendingRows = rowsToDelete || selectedRows;

    const onConfirmDelete = async () => {
        if (isMountedRef.current) {
            setErrorMessage(null);
            setErrorLines([]);
        }

        // mic is derived from the Authorization header by cdm-mw; crd travels as the
        // userCrd param (same contract as Symbol MPID Clearing) — neither is sent per row.
        const appointments = pendingRows.map((row) => ({
            mmid: row.mmid,
            symbol: row.symbol,
            mmType: row.mmType
        }));

        // The grid can show one appointment as two rows when GBO and Pillar disagree on its MM
        // type (the transient after a retype). CS 11009 §11009.6.3 does not support repeating a
        // symbol and MMID in one Delete array, and a GBO row is keyed (crd, mmid, symbol) with
        // mm_type overwriteable, so those two rows are one appointment queued for deletion twice
        // (TOP-5791). Name the offending selection here rather than dispatching it.
        const duplicates = findDuplicateAppointmentIndexes(appointments);

        if (duplicates.size > 0) {
            if (isMountedRef.current) {
                // The grid sits behind this modal, so the selection cannot be changed without
                // closing it first. Say so, or the instruction cannot be followed.
                setErrorMessage('The same MMID and symbol cannot be deleted twice in one request. '
                    + 'Close this dialog, deselect the duplicate row, then delete again:');
                setErrorLines([...duplicates].map((index) => ({
                    label: [appointments[index].mmid, appointments[index].symbol].filter(Boolean).join(' / '),
                    message: 'selected more than once'
                })));
            }
            return;
        }

        try {
            const action = await deleteMmAppointments({userCrd, appointments});
            const {successCount, errorCount, total, errorLines: failures, message: gboSummary} =
                summarizeBatchResult(action?.payload?.data?.data, appointments.length);

            // DELETE returns 207 (a 2xx) on partial failure, so per-row errors arrive
            // here on the success path, not via catch().
            if (errorCount === 0) {
                // The grid re-fetch right after this toast won't reflect the removal yet: a
                // pre-open delete clears from Pillar asynchronously, and a post-open delete is
                // HELD by GBO for next-day processing (the row stays present + active on the
                // read). Either way the still-present row isn't a failure.
                //
                // GBO states the actual disposition in its write-response Summary message (e.g.
                // "1 appointment(s) received and will be processed for next day trading.") — the
                // read side carries no such flag. cdm-mw passes that message through, so prefer it
                // verbatim: it's authoritative per request. Fall back to the generic both-windows
                // line only when GBO returned no summary.
                const successMessage = gboSummary
                    || (`${successCount} MM appointment${successCount === 1 ? '' : 's'} submitted for deletion. `
                        + 'Deletions before the opening auction apply within ~1 minute; '
                        + 'deletions during trading hours take effect at next-day processing.');

                setTimeout(() => {
                    if (isMountedRef.current) {
                        onClose(true, successMessage);
                    }
                }, 0);
                return;
            }

            if (isMountedRef.current) {
                if (successCount > 0) {
                    hadPartialSuccessRef.current = true;
                    // Narrow the retry set to just the rows that failed, so a re-Confirm never
                    // re-submits the rows GBO already accepted. mmType stays in the key to match
                    // the error records cdm-mw echoes back, though the duplicate gate above now
                    // makes mmid+symbol unique within a request on its own.
                    const rowKey = (row) => `${row.mmid}|${row.symbol}|${row.mmType}`;
                    const failedKeys = new Set(failures.map(rowKey));
                    setRowsToDelete(
                        appointments.filter((row) => failedKeys.has(rowKey(row)))
                    );
                }
                setAllFailed(successCount === 0);
                setErrorMessage(
                    successCount > 0
                        ? `${successCount} of ${total} appointment(s) deleted — ${errorCount} failed:`
                        : `Failed to delete ${errorCount} appointment(s):`
                );
                setErrorLines(failures);
            }
        } catch (error) {
            // Mirror the Add modal: a rejected request wraps the detail in an
            // `error` envelope (RFC-7807 problem+json), so read that before the
            // bare Error message.
            const errorMsg = error?.error?.detail
                || error?.error?.title
                || error?.error?.message
                || error?.message
                || 'Failed to delete appointments. Please try again.';

            if (isMountedRef.current) {
                setErrorMessage(errorMsg);
                setErrorLines([]);
            }
        }
    };

    return (
        <NyModal
            show={show}
            hideModal={hideModal}
            title='Confirm Delete'
            className='modal-medium'>
            <div className='modal-body'>
                {!allFailed && (
                    <p className='space-bottom-1'>
                        Are you sure you want to delete <strong>{pendingRows.length}</strong> appointment{pendingRows.length !== 1 ? 's' : ''}?
                    </p>
                )}

                {errorMessage && (
                    <div className='alert alert-danger space-bottom-1'>
                        <div>{errorMessage}</div>
                        {errorLines.length > 0 && (
                            <ul className='space-top-1 space-bottom-0'>
                                {errorLines.map((line, index) => (
                                    <li key={index}>
                                        {line.label ? <strong>{line.label}: </strong> : null}{line.message}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>

            <div className='modal-footer'>
                {allFailed ? (
                    // Every row was rejected — Confirm would just repeat the same
                    // failure, so Close is the only action. hadPartialSuccessRef
                    // preserves any earlier-attempt successes so the grid still
                    // refreshes on close.
                    <button
                        className='btn btn-default'
                        onClick={() => onClose(hadPartialSuccessRef.current)}>
                        Close
                    </button>
                ) : (
                    <>
                        <button
                            className='btn btn-danger'
                            onClick={onConfirmDelete}
                            disabled={isDeleting}>
                            {isDeleting ? 'Deleting...' : 'Confirm Delete'}
                        </button>
                        <button
                            className='btn btn-default'
                            onClick={() => onClose(hadPartialSuccessRef.current)}
                            disabled={isDeleting}>
                            Cancel
                        </button>
                    </>
                )}
            </div>
        </NyModal>
    );
};

DeleteConfirmationModalComponent.propTypes = {
    userCrd: PropTypes.number.isRequired,
    selectedRows: PropTypes.arrayOf(PropTypes.shape({
        mmid: PropTypes.string.isRequired,
        symbol: PropTypes.string.isRequired,
        mmType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
    })).isRequired,
    show: PropTypes.bool.isRequired,
    hideModal: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    deleteMmAppointments: PropTypes.func.isRequired,
    mmAppointments: PropTypes.shape({
        isDeleting: PropTypes.bool,
        deleteError: PropTypes.object
    })
};

export const DeleteConfirmationModal = connect(
    ['mmAppointments'],
    {deleteMmAppointments}
)(DeleteConfirmationModalComponent);
